import { useCallback } from "react";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import {
  buildFilterGroupFromColumnState,
  type FilterGroup,
} from "@/lib/filters";
import type { NullSort } from "@/lib/process-query-params";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import type { WebsitePage } from "./api/use-website-page-search";
import { type BaseListFilters, useListFilters } from "./use-list-filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Default sort settings
const DEFAULT_SORT_COLUMN = "url";
const DEFAULT_SORT_DIRECTION = "asc";

// SessionStorage keys
const WEBPAGE_LIST_FILTERS_KEY = "webpages::list::filters";
const WEBPAGE_METRICS_KEY = "webpages::list::metrics";

const DEFAULT_METRICS: Array<keyof WebsitePageMetrics> = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
];

// Webpage-specific list filters
export interface WebpageListFilters {
  channel_id?: string;
  start_date?: string;
  end_date?: string;
}

// Query params for webpage API
export interface WebpagesQueryParams {
  sort_by?: string;
  sort_direction?: SortDirection;
  search?: string;
  limit?: number;
  offset?: number;
  channel_id?: string;
  start_date?: string;
  end_date?: string;
  null_sort?: NullSort;
  metrics?: Array<keyof WebsitePageMetrics>;
  metric_filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
  };
  formulas?: Array<{
    id: string;
    name: string;
    formulaAST: unknown;
    dataType: string;
    description?: string;
  }>;
  filters?: string;
  [key: string]: unknown;
}

// Extend base list filters with webpage-specific filters
interface ExtendedWebpageListFilters
  extends BaseListFilters,
    WebpageListFilters {}

/**
 * Hook for managing webpage metrics
 * @returns Methods for getting and setting selected metrics
 */
export function useWebpageMetrics() {
  // Get and set metrics using sessionStorage for tab-specific behavior
  const [selectedMetrics, setSelectedMetrics] = useTrackedSessionStorage<
    Array<keyof WebsitePageMetrics>
  >(WEBPAGE_METRICS_KEY, DEFAULT_METRICS);

  return {
    selectedMetrics,
    setSelectedMetrics,
    getDefaultMetrics: () => DEFAULT_METRICS,
  };
}

/**
 * Hook for managing webpage list filters
 * @returns Filters and utilities for managing webpage list filters
 */
export function useWebpageListFilters() {
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams: getBaseQueryParams,
  } = useListFilters<
    ExtendedWebpageListFilters,
    UnifiedTableColumn<WebsitePage>,
    WebpagesQueryParams
  >({
    storageKey: WEBPAGE_LIST_FILTERS_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });

  // Helper function to build query params
  function buildQueryParams(
    filters: ExtendedWebpageListFilters,
    options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<WebsitePage>[];
      limit?: number;
    },
  ): WebpagesQueryParams {
    const { searchTerm, columns = [], limit = 25 } = options || {};

    // Sort handling – follow same logic as campaigns list
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sort column no longer exists in current column set
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition to get null_sort behaviour
    const column = columns.find((col) => col.key === sortCol);

    const params: WebpagesQueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir,
      null_sort: (column?.nullSort || "last") as NullSort,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // Add webpage-specific filters
    if (filters.channel_id && filters.channel_id !== "all") {
      params.channel_id = filters.channel_id;
    }

    // Handle date filters
    if (filters.start_date) {
      params.start_date = filters.start_date;
    }

    if (filters.end_date) {
      params.end_date = filters.end_date;
    }

    // Extract formulas from columns
    const formulaColumns = columns.filter(
      (col) => col.isFormulaColumn && col.formulaDefinition,
    );
    if (formulaColumns.length > 0) {
      params.formulas = formulaColumns.map((col) => col.formulaDefinition!);
    }

    // Structured filters JSON
    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  }

  // Wrapper for getQueryParams that accepts metrics and filters
  const getQueryParams = useCallback(
    (options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<WebsitePage>[];
      limit?: number;
      metrics?: Array<keyof WebsitePageMetrics>;
      metric_filters?: WebpagesQueryParams["metric_filters"];
      formulas?: WebpagesQueryParams["formulas"];
    }) => {
      const { metrics, metric_filters, formulas, ...restOptions } =
        options || {};

      // Get base query params from filters
      const params = getBaseQueryParams(restOptions) as WebpagesQueryParams;

      // Add metrics and metric_filters if provided
      if (metrics) {
        params.metrics = metrics;
      }

      if (metric_filters) {
        params.metric_filters = metric_filters;
      }

      // Add formulas if provided
      if (formulas) {
        params.formulas = formulas;
      }

      return params;
    },
    [getBaseQueryParams],
  );

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  };
}
